import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { CartAddedToast } from '../components/CartAddedToast/CartAddedToast';
import {
  DIGIRASA_SUBSCRIPTION_CART_CLEARED_EVENT,
  SUBSCRIPTION_CART_STORAGE_KEY,
} from './subscriptionCartStorage';
import {
  canChangeSubscriptionCartSlot,
  normalizeSubscriptionCartItem,
  subscriptionCartLineKey,
} from '../utils/subscriptionCartItems';

const SubscriptionCartContext = createContext({
  subscriptionCartItems: [],
  addToSubscriptionCart: () => {},
  removeFromSubscriptionCart: () => {},
  updateSubscriptionCartQty: () => {},
  updateSubscriptionCartSlot: () => {},
  clearSubscriptionCart: () => {},
  subscriptionCartCount: 0,
  subscriptionCartTotal: 0,
});

const TOAST_DURATION_MS = 5500;

export const useSubscriptionCart = () => useContext(SubscriptionCartContext);

function readStoredItems() {
  try {
    const parsed = JSON.parse(localStorage.getItem(SUBSCRIPTION_CART_STORAGE_KEY));
    return Array.isArray(parsed) ? parsed.map((item) => normalizeSubscriptionCartItem(item)) : [];
  } catch {
    return [];
  }
}

export const SubscriptionCartProvider = ({ children }) => {
  const [items, setItems] = useState(readStoredItems);
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(SUBSCRIPTION_CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    const onCleared = () => {
      setItems((prev) => (prev.length === 0 ? prev : []));
    };
    window.addEventListener(DIGIRASA_SUBSCRIPTION_CART_CLEARED_EVENT, onCleared);
    return () => window.removeEventListener(DIGIRASA_SUBSCRIPTION_CART_CLEARED_EVENT, onCleared);
  }, []);

  const dismissToast = useCallback(() => {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
    setToast(null);
  }, []);

  const showToast = useCallback(({ name, isRepeat, cartCount }) => {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
    }
    setToast({
      name: name || 'Item',
      isRepeat,
      cartCount,
      key: Date.now(),
      kind: 'subscription',
    });
    toastTimerRef.current = window.setTimeout(() => {
      toastTimerRef.current = null;
      setToast(null);
    }, TOAST_DURATION_MS);
  }, []);

  useEffect(() => () => dismissToast(), [dismissToast]);

  const addToSubscriptionCart = useCallback((item, options = {}) => {
    const { quantity = 1, slotId, showToast: shouldToast = true, mode = 'add' } = options;
    const incoming = normalizeSubscriptionCartItem(item, { quantity, slotId });
    const lineKey = subscriptionCartLineKey(incoming);
    const existing = items.find((row) => subscriptionCartLineKey(row) === lineKey);
    const isRepeat = Boolean(existing);
    const nextCount = mode === 'upsert' && existing
      ? items.reduce((sum, row) => sum + row.quantity, 0)
      : items.reduce((sum, row) => sum + row.quantity, 0) + incoming.quantity;

    setItems((prev) => {
      const prevExisting = prev.find((row) => subscriptionCartLineKey(row) === lineKey);
      if (prevExisting && mode === 'upsert') {
        return prev.map((row) => (
          subscriptionCartLineKey(row) === lineKey
            ? { ...incoming, quantity: prevExisting.quantity }
            : row
        ));
      }
      if (prevExisting) {
        return prev.map((row) => (
          subscriptionCartLineKey(row) === lineKey
            ? { ...row, ...incoming, quantity: row.quantity + incoming.quantity }
            : row
        ));
      }
      return [...prev, incoming];
    });

    const onCartPage =
      typeof window !== 'undefined'
      && (window.location.pathname === '/subscription-cart'
        || window.location.pathname === '/add-subscription');
    if (shouldToast && !onCartPage) {
      showToast({
        name: incoming.name,
        isRepeat,
        cartCount: nextCount,
      });
    }
  }, [items, showToast]);

  const removeFromSubscriptionCart = useCallback((id, slotId) => {
    setItems((prev) => prev.filter((row) => {
      if (String(row.fullDayGroupId || '') === String(id) || String(row.id) === String(id)) {
        if (!slotId || row.kind === 'full_day') return false;
        return row.slotId !== slotId;
      }
      return true;
    }));
  }, []);

  const updateSubscriptionCartQty = useCallback((id, slotId, qty) => {
    if (qty < 1) {
      removeFromSubscriptionCart(id, slotId);
      return;
    }
    setItems((prev) => prev.map((row) => {
      const sameId = String(row.id) === String(id) || String(row.fullDayGroupId || '') === String(id);
      if (!sameId) return row;
      if (slotId && row.kind !== 'full_day' && row.slotId !== slotId) return row;
      return { ...row, quantity: qty };
    }));
  }, [removeFromSubscriptionCart]);

  const updateSubscriptionCartSlot = useCallback((id, fromSlotId, toSlotId) => {
    setItems((prev) => {
      if (!toSlotId || fromSlotId === toSlotId) return prev;
      const line = prev.find((row) => String(row.id) === String(id) && row.slotId === fromSlotId);
      if (!line || !canChangeSubscriptionCartSlot(line)) return prev;
      const without = prev.filter((row) => !(String(row.id) === String(id) && row.slotId === fromSlotId));
      const moved = normalizeSubscriptionCartItem({ ...line, slotId: toSlotId });
      const existing = without.find((row) => subscriptionCartLineKey(row) === subscriptionCartLineKey(moved));
      if (existing) {
        return without.map((row) => (
          subscriptionCartLineKey(row) === subscriptionCartLineKey(moved)
            ? { ...row, quantity: row.quantity + line.quantity }
            : row
        ));
      }
      return [...without, moved];
    });
  }, []);

  const clearSubscriptionCart = useCallback(() => {
    setItems((prev) => (prev.length === 0 ? prev : []));
  }, []);

  const subscriptionCartCount = items.reduce((sum, row) => sum + row.quantity, 0);
  const subscriptionCartTotal = items.reduce((sum, row) => sum + row.price * row.quantity, 0);

  return (
    <SubscriptionCartContext.Provider
      value={{
        subscriptionCartItems: items,
        addToSubscriptionCart,
        removeFromSubscriptionCart,
        updateSubscriptionCartQty,
        updateSubscriptionCartSlot,
        clearSubscriptionCart,
        subscriptionCartCount,
        subscriptionCartTotal,
      }}
    >
      {children}
      <CartAddedToast toast={toast} onDismiss={dismissToast} />
    </SubscriptionCartContext.Provider>
  );
};
